import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { useTranslation } from "react-i18next";

const COOKIE_CONSENT_KEY = "piefolio_cookie_consent";

type ConsentStatus = "accepted" | "rejected" | null;

const CookieConsent = () => {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!stored) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
    setConsentStatus(stored as ConsentStatus);
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "accepted");
    setConsentStatus("accepted");
    setIsVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "rejected");
    setConsentStatus("rejected");
    setIsVisible(false);
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <div className="max-w-4xl mx-auto">
            <div className="bg-card border border-border rounded-2xl shadow-2xl p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 flex-shrink-0">
                  <Cookie className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">{t("cookie.title")}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {t("cookie.description")}{" "}
                        <Link to="/cookies" className="text-primary hover:underline font-medium">{t("cookie.cookiePolicy")}</Link>
                        {" "}{t("cookie.and")}{" "}
                        <Link to="/privacy" className="text-primary hover:underline font-medium">{t("cookie.privacyPolicy")}</Link>
                        {" "}{t("cookie.toLearnMore")}
                      </p>
                    </div>
                    <button onClick={handleClose} className="text-muted-foreground hover:text-foreground transition-colors p-1 -mt-1 -mr-1" aria-label="Close cookie banner">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button onClick={handleAccept} className="font-semibold">{t("cookie.acceptAll")}</Button>
                    <Button onClick={handleReject} variant="outline" className="font-semibold">{t("cookie.rejectNonEssential")}</Button>
                    <Link to="/cookies" className="sm:ms-auto">
                      <Button variant="ghost" className="w-full sm:w-auto text-muted-foreground">{t("cookie.cookieSettings")}</Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;

export const useCookieConsent = () => {
  const [consent, setConsent] = useState<ConsentStatus>(null);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    setConsent(stored as ConsentStatus);
  }, []);

  return {
    hasConsent: consent === "accepted",
    consentStatus: consent,
  };
};
